import dayjs from "dayjs";
import { OrderRowType } from "./OrderData";

// Pull a human-readable message out of an axios error (or anything else that was thrown).
export const getErrorMessage = (error: any, fallback = "Something went wrong. Please try again.") =>
  error?.response?.data?.message ||
  error?.response?.data?.detail ||
  error?.response?.data?.error ||
  (typeof error?.message === "string" && !error.message.startsWith("Request failed") ? error.message : "") ||
  fallback;

// Save a Blob as a file and release the object URL afterwards.
export const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Use the server-provided filename when there is one.
export const filenameFromHeaders = (headers: any, fallback: string) => {
  const disposition: string = headers?.["content-disposition"] || "";
  const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/);
  return match?.[1] ? decodeURIComponent(match[1]) : fallback;
};

// Blob downloads that fail come back as a Blob containing JSON; read the message out of it.
export const readBlobError = async (error: any, fallback: string) => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const json = JSON.parse(await data.text());
      return json?.message || json?.detail || json?.error || fallback;
    } catch {
      return fallback;
    }
  }
  return getErrorMessage(error, fallback);
};

export const chunk = <T,>(items: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
};

// ─── Status presentation ─────────────────────────────────────────────────────

const STATUS_STYLE: Record<string, { label: string; color: string }> = {
  ready_to_ship: { label: "Ready to Ship", color: "#607d8b" },
  booked: { label: "Booked", color: "#3949ab" },
  pickup_raised: { label: "Pickup Scheduled", color: "#ed6c02" },
  picked_up: { label: "Picked Up", color: "#00897b" },
  not_picked: { label: "Not Picked", color: "#e65100" },
  in_transit: { label: "In Transit", color: "#0288d1" },
  exception: { label: "Exception", color: "#d32f2f" },
  "out for delivery": { label: "Out for Delivery", color: "#6a1b9a" },
  out_for_delivery: { label: "Out for Delivery", color: "#6a1b9a" },
  delivered: { label: "Delivered", color: "#2e7d32" },
  cancelled: { label: "Cancelled", color: "#757575" },
  rto: { label: "RTO", color: "#7b1fa2" },
  return_initiated: { label: "Return Initiated", color: "#ed6c02" },
  return_in_transit: { label: "Return In Transit", color: "#0288d1" },
  return_out_for_delivery: { label: "Return Out for Delivery", color: "#6a1b9a" },
  return_delivered: { label: "Return Delivered", color: "#2e7d32" },
};

export const getStatusStyle = (status?: string | null) => {
  const key = (status ?? "").toLowerCase().trim();
  return (
    STATUS_STYLE[key] ?? {
      label: key ? key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—",
      color: "#607d8b",
    }
  );
};

export const formatOrderDate = (value?: string | null) => {
  if (!value) return "—";
  const d = dayjs(value);
  return d.isValid() ? d.format("DD MMM YYYY") : String(value).split("T")[0];
};

// ─── Search ──────────────────────────────────────────────────────────────────

// Flatten every value in an order (including nested objects like package_details) plus the
// text we display for it (status label, formatted date), so search matches anything visible.
export const buildOrderSearchText = (order: OrderRowType) => {
  const parts: string[] = [getStatusStyle(order.status).label, formatOrderDate(order.track_time)];
  const walk = (value: unknown) => {
    if (value === null || value === undefined) return;
    if (typeof value === "object") Object.values(value as object).forEach(walk);
    else parts.push(String(value));
  };
  walk(order);
  return parts.join(" | ").toLowerCase();
};

// ─── CSV export ──────────────────────────────────────────────────────────────

const CSV_COLUMNS: { header: string; value: (r: OrderRowType & Record<string, any>) => unknown }[] = [
  { header: "Order ID", value: (r) => r.id },
  { header: "LR Number", value: (r) => r.lr_number },
  { header: "AWB", value: (r) => r.awb },
  { header: "Invoice No.", value: (r) => (r.invoice_numbers ?? []).join(", ") },
  { header: "Status", value: (r) => getStatusStyle(r.status).label },
  { header: "Channel", value: (r) => r.order_channel },
  { header: "Courier", value: (r) => r.vendor },
  { header: "Receiver", value: (r) => r.receiver },
  { header: "From", value: (r) => r.from },
  { header: "To", value: (r) => r.to },
  { header: "Amount", value: (r) => r.amount },
  { header: "Pickup Number", value: (r) => r.pickup_number },
  { header: "Description", value: (r) => r.description },
  { header: "Remark", value: (r) => r.comments },
];

const csvCell = (value: unknown) => {
  const text = value === null || value === undefined ? "" : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const exportOrdersCsv = (rows: OrderRowType[], filename: string) => {
  const lines = [
    CSV_COLUMNS.map((c) => c.header).join(","),
    ...rows.map((r) => CSV_COLUMNS.map((c) => csvCell(c.value(r as any))).join(",")),
  ];
  // BOM so Excel opens the file as UTF-8 (₹, non-English names, ...).
  downloadBlob(new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" }), filename);
};

// ─── Readable API errors ─────────────────────────────────────────────────────

// "receiver_mobile_number" → "Receiver mobile number"
export const humanizeField = (key: string) => {
  const text = String(key).replace(/[_.]+/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export interface ApiIssue {
  field?: string;
  message: string;
}

// Turn whatever the backend sent (string, {message}, {detail}, {field: [msgs]}, arrays, nested
// objects) into a flat list of readable issues.
export const flattenApiErrors = (data: unknown, field?: string): ApiIssue[] => {
  if (data === null || data === undefined || data === "") return [];
  if (typeof data === "string" || typeof data === "number" || typeof data === "boolean") {
    return [{ field, message: String(data) }];
  }
  if (Array.isArray(data)) return data.flatMap((item) => flattenApiErrors(item, field));
  if (typeof data === "object") {
    const obj = data as Record<string, unknown>;
    for (const key of ["message", "detail", "error", "msg"]) {
      if (typeof obj[key] === "string" && obj[key]) return [{ field, message: obj[key] as string }];
    }
    return Object.entries(obj)
      .filter(([k]) => !["status", "success", "code", "line_num"].includes(k))
      .flatMap(([k, v]) => flattenApiErrors(v, field ? `${field} ${humanizeField(k).toLowerCase()}` : humanizeField(k)));
  }
  return [];
};
